/** Live-Verbindung des Mieterportals zum LadenHub: Verkäufe und Hinweise kommen per WebSocket, sonst Polling alle 30 s. */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { HubNachricht } from "../../worker/durable/LadenHub";

type Verkauf = Extract<HubNachricht, { art: "verkauf" }>;
const listener = new Set<(v: Verkauf) => void>();

/** Komponenten (z. B. Start) hängen sich hier ein, um „Verkauft!“ sofort zu zeigen. */
export function useVerkaufsMeldung(cb: (v: Verkauf) => void) {
  const ref = useRef(cb);
  ref.current = cb;
  useEffect(() => {
    const f = (v: Verkauf) => ref.current(v);
    listener.add(f);
    return () => {
      listener.delete(f);
    };
  }, []);
}

export function useMieterLive(): boolean {
  const qc = useQueryClient();
  const [verbunden, setVerbunden] = useState(false);
  useEffect(() => {
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let beendet = false;
    let versuch = 0;
    const poll = setInterval(() => void qc.invalidateQueries({ queryKey: ["mieter", "start"] }), 30_000);
    const verbinde = () => {
      if (beendet) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      ws = new WebSocket(`${proto}://${location.host}/api/mieter/ws`);
      ws.onopen = () => {
        versuch = 0;
        setVerbunden(true);
      };
      ws.onmessage = (ev) => {
        try {
          const n = JSON.parse(String(ev.data)) as HubNachricht;
          if (n.art === "verkauf" || n.art === "hinweis") void qc.invalidateQueries({ queryKey: ["mieter"] });
          if (n.art === "verkauf") for (const f of listener) f(n);
        } catch {
          // ignorieren
        }
      };
      ws.onclose = () => {
        setVerbunden(false);
        if (beendet) return;
        versuch++;
        timer = setTimeout(verbinde, Math.min(30_000, 1000 * 2 ** Math.min(versuch, 5)));
      };
      ws.onerror = () => ws?.close();
    };
    verbinde();
    const ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send("ping"), 25_000);
    return () => {
      beendet = true;
      clearInterval(ping);
      clearInterval(poll);
      if (timer) clearTimeout(timer);
      ws?.close();
    };
  }, [qc]);
  return verbunden;
}
